import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { NominatimResult, Route } from "../../types/route";
import { Transportation } from "../../types/transportation";
import * as Nominatim from 'nominatim-browser';
import Geolookup from 'react-geolookup-v2';


const FormRoute: React.FC = ({ id, addRoute, transportations, checkInProgress, checkInIsCompleted, origin, t }: any) => {

    const getInitialState = (): Route => ({
        transportation: { id: 0 },
        destination: {
            name: id === 1 ? 'Barcelona, España' : '',
            latitude: id === 1 ? '41.3851' : '0',
            longitude: id === 1 ? '2.1734' : '0'
        },
        origin: {
            name: origin?.name || '',
            latitude: origin?.latitude || '0',
            longitude: origin?.longitude || '0'
        }
    });
    const [resetKey, setResetKey] = useState<number>(Math.random());

    const [route, setRoute] = useState<Route>(getInitialState());

    useEffect(() => {
        if (!checkInProgress && checkInIsCompleted) {
            setRoute(getInitialState());
            setResetKey(Math.random());
        }
    }, [checkInIsCompleted, checkInProgress]);

    const colspan = id > 1 ? 'col-span-2' : 'flex-wrap -mx-3';

    const onSuggestsLookup = (userInput: string): Promise<NominatimResult[]> => {
        if (!userInput || userInput.length < 3) {
            return Promise.resolve([]);
        }

        return Nominatim.geocode({
            q: userInput,
            addressdetails: true,
            limit: 10
        }).then((results: any[]) => {
            const castedResults = results as NominatimResult[];
            const seenNames = new Set<string>();
            const uniqueResults: NominatimResult[] = [];

            castedResults.forEach((result) => {
                const addr = result.address;

                const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.hamlet;
                const stateName = addr.state;
                const countryName = addr.country;
                const shortName = [cityName, stateName, countryName]
                    .filter(Boolean)
                    .join(', ');

                if (shortName && !seenNames.has(shortName)) {
                    seenNames.add(shortName);
                    uniqueResults.push({
                        ...result,
                        display_name: shortName 
                    });
                }
            });

            return uniqueResults.slice(0, 5);
        });
    };

    const onGeocodeSuggest = (type: 'origin' | 'destination', currentId: number, suggest: any) => {
        if (!suggest || !suggest.raw) return null;

        const geocoded = {
            isFixture: false,
            placeId: suggest.placeId,
            label: suggest.raw.display_name,
            raw: suggest.raw,
            location: {
                lat: suggest.raw.lat,
                lng: suggest.raw.lon
            }
        };


        const newRouteData = {
            ...route,
            id: currentId - 1,
            [type]: {
                name: geocoded.label,
                latitude: geocoded.location.lat,
                longitude: geocoded.location.lng
            }
        };

        setRoute(newRouteData);

        if (newRouteData.destination.name && newRouteData.origin.name) {
            addRoute(newRouteData);
        }

        return geocoded;
    };


    const getSuggestLabel = (suggest: any) => {
        if (!suggest) return '';

        const parts = suggest.display_name.split(',');

        if (parts.length > 3) {
            return `${parts[0].trim()}, ${parts[parts.length - 3].trim()}, ${parts[parts.length - 1].trim()}`;
        }

        return suggest.display_name;
    };

    return (
        <>
            {id === 1 && (
                <div className="flex">
                    <div className="w-full md:px-3 md:mb-6 md:mb-0">
                        <Geolookup
                            key={`origin-${id}-${resetKey}`}
                            inputClassName="geolookup__input--nominatim"
                            disableAutoLookup={false}
                            suggestsHiddenClassName={"hidden"}
                            required={true}
                            country=".es"
                            placeholder={t('formRoute.origin')}
                            onSuggestsLookup={onSuggestsLookup}
                            getSuggestLabel={getSuggestLabel}
                            onSuggestSelect={(suggest: any) => {
                                if (suggest) {
                                    onGeocodeSuggest('origin', id, suggest);
                                }
                            }}
                            ignoreTab={true}
                            radius="20"
                        />
                    </div>
                </div>
            )}

            <div className={"flex " + colspan}>
                <div className="w-full px-3 md:mb-6 md:mb-0">
                    <Geolookup
                        key={`dest-${id}-${resetKey}`}
                        ignoreEnter={true}
                        inputClassName="geolookup__input--nominatim"
                        disableAutoLookup={false}
                        suggestsHiddenClassName={"hidden"}
                        country={"es"}
                        required={true}
                        disabled={id === 1} 
                        initialValue={id === 1 ? 'Barcelona, España' : ''}
                        onSuggestsLookup={onSuggestsLookup}
                        placeholder={t('formRoute.destination')}
                        onGeocodeSuggest={(result: any) => onGeocodeSuggest('destination', id, result)}
                        getSuggestLabel={getSuggestLabel}
                        radius="20"
                    />
                </div>
            </div>

            <div className="">
                <div className="relative">
                    <select
                        className="block appearance-none w-full py-3.5 px-4 pr-8 rounded-xl leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        value={route.transportation.id}
                        onChange={(e) => {
                            const transId = parseInt(e.target.value);
                            const updatedRoute = {
                                ...route,
                                transportation: { id: transId }
                            };
                            setRoute(updatedRoute);
                            if (updatedRoute.destination.name && updatedRoute.origin.name) {
                                addRoute(updatedRoute);
                            }
                        }}
                    >
                        <option value="0">{t("formTravel.transport")}</option>
                        {transportations.map((transport: Transportation) => (
                            <option key={`${id}_trans_${transport.id}`} value={transport.id}>
                                {t("formTravel." + transport.name)}
                            </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-0 px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTranslation('common')(FormRoute);